// src/components/warranties/WarrantiesSection.jsx
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import Swal from "sweetalert2";

import {
    Box,
    Button,
    Divider,
    Stack,
    Typography,
    TextField,
    MenuItem,
    InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { djangoApi } from "../../utils/axios/axios.utils";
import WarrantyCreateForm from "./WarrantyCreateForm";
import WarrantyListTable from "./WarrantyListTable";

const STATUS_OPTIONS = [
    { value: "", label: "Todos" },
    { value: "OPEN", label: "Aberta" },
    { value: "PENDING", label: "Pendente" },
    { value: "ATFACTORY", label: "Na fábrica" },
    { value: "SENDTOFACTORY", label: "Enviar para fábrica" },
    { value: "RETURNSELLER", label: "Devolver para revendedora" },
    { value: "SOLVED", label: "Resolvido" },
    { value: "CANCELED", label: "Anulado" },
];

function buildSelectedProductFromSnapshot(w) {
    const snap =
        w?.product_snapshot ??
        w?.kit_product_snapshot ??
        null;

    if (!snap) return null;

    const kitProductPk =
        snap?.pk ??
        snap?.kit_product_pk ??
        w?.kit_product_id ??
        w?.kit_product_pk ??
        w?.kit_product?.pk ??
        w?.kit_product?.id ??
        null;

    return {
        ...snap,

        // ID do KitProduct
        pk: kitProductPk,
        kit_product_pk: kitProductPk,

        // ID do produto original
        product_id_produto_pk:
            snap?.product_id_produto_pk ??
            snap?.object_id ??
            null,

        object_id:
            snap?.object_id ??
            snap?.product_id_produto_pk ??
            null,

        product_id_produto:
            snap?.product_id_produto ??
            w?.product_code ??
            null,

        content_type_id:
            snap?.content_type_id ??
            null,

        content_type__model:
            snap?.content_type__model ??
            null,

        product_id_produto_color:
            snap?.product_id_produto_color ??
            null,

        product_tamanho:
            snap?.product_tamanho ??
            null,

        sell_price:
            snap?.sell_price ??
            null,

        quantity:
            snap?.quantity ??
            null,

        quantity_sold:
            snap?.quantity_sold ??
            null,

        quantity_returned:
            snap?.quantity_returned ??
            null,

        qty_not_returned:
            snap?.qty_not_returned ??
            null,

        kit_id:
            snap?.kit_id ??
            null,

        kit__kit_number:
            snap?.kit__kit_number ??
            null,

        kit__pay_date:
            snap?.kit__pay_date ??
            null,

        seller_id:
            snap?.seller_id ??
            snap?.kit__seller_id ??
            w?.seller_id ??
            w?.seller?.id ??
            null,

        seller_nome:
            snap?.seller_nome ??
            snap?.kit__seller__nome ??
            w?.seller_nome ??
            w?.seller?.nome ??
            null,

        kit__seller_id:
            snap?.kit__seller_id ??
            snap?.seller_id ??
            w?.seller_id ??
            w?.seller?.id ??
            null,

        kit__seller__nome:
            snap?.kit__seller__nome ??
            snap?.seller_nome ??
            w?.seller_nome ??
            w?.seller?.nome ??
            null,

        imageUrl:
            snap?.imageUrl ??
            null,
    };
}

function buildSellerFromWarranty(w, sellerFromTop) {
    const warrantySellerId =
        w?.seller_id ??
        w?.seller?.id ??
        w?.seller_pk ??
        w?.product_snapshot?.seller_id ??
        w?.product_snapshot?.kit__seller_id ??
        null;

    const warrantySellerName =
        w?.seller_nome ??
        w?.seller_name ??
        w?.seller?.nome ??
        w?.product_snapshot?.seller_nome ??
        w?.product_snapshot?.kit__seller__nome ??
        null;

    // Em edição, sempre utiliza a vendedora gravada na garantia.
    if (warrantySellerId != null) {
        return {
            id: warrantySellerId,
            nome:
                warrantySellerName ||
                `Vendedora #${warrantySellerId}`,
        };
    }

    // Em criação, utiliza a vendedora selecionada no topo.
    if (sellerFromTop?.id != null) {
        return {
            ...sellerFromTop,
            id: sellerFromTop.id,
        };
    }

    return null;
}

export default function WarrantiesSection({
    seller, // pode ser null
    selectedProduct, // pode ser null
    theme,
    colors,
    subtleBorder,
    softText,
    fmtBRL,
    onDone,
    onEditWarranty,
    onRequestCloseEdit
}) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const requestControllerRef = useRef(null);

    const [editingWarranty, setEditingWarranty] = useState(null);

    useEffect(() => {
        if (typeof onRequestCloseEdit === "function") {
            onRequestCloseEdit(() => setEditingWarranty(null));
        }
    }, [onRequestCloseEdit]);

    const [filters, setFilters] = useState({
        status: "",
        lot_code: "",
        q: "",
    });

    useEffect(() => {
        if (!selectedProduct?.pk) {
            setEditingWarranty(null); // ✅ fecha edição quando remove produto
        }
    }, [selectedProduct?.pk]);


    const fetchParams = useMemo(() => {
        const params = {};
        // ✅ se selecionou vendedora no topo, filtra
        if (seller?.id) params.seller_id = seller.id;
        if (filters.status) params.status = filters.status;
        if (filters.lot_code?.trim()) params.lot_code = filters.lot_code.trim();
        return params;
    }, [seller?.id, filters.status, filters.lot_code]);

    const fetchWarranties = useCallback(async () => {
        requestControllerRef.current?.abort();

        const controller = new AbortController();
        requestControllerRef.current = controller;

        try {
            setLoading(true);
            setError("");

            const res = await djangoApi.get(
                "products/warranties/",
                {
                    params: fetchParams,
                    signal: controller.signal,
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = res?.data;

            const list = Array.isArray(data)
                ? data
                : Array.isArray(data?.results)
                    ? data.results
                    : [];

            setRows(list);
        } catch (err) {
            const canceled =
                err?.name === "CanceledError" ||
                err?.name === "AbortError" ||
                err?.code === "ERR_CANCELED";

            if (canceled) return;

            console.error("Erro ao listar garantias", err);
            setError(
                "Falha ao carregar garantias. Veja o console."
            );
            setRows([]);
        } finally {
            if (requestControllerRef.current === controller) {
                setLoading(false);
            }
        }
    }, [fetchParams]);

    useEffect(() => {
        fetchWarranties();

        return () => {
            requestControllerRef.current?.abort();
        };
    }, [fetchWarranties]);

    // ✅ busca local
    const filteredRows = useMemo(() => {
        const q = String(filters.q || "").trim().toLowerCase();
        if (!q) return rows;

        return rows.filter((r) => {
            const hay = [
                r.number_ref,
                r.product_code,
                r.sale_month,
                r.product_description,
                r.problem_reported,
                r.lot_code,
                r.status,
                r.customer_contact,
                r.notes,
                r?.product_snapshot?.product_id_produto,
                r?.product_snapshot?.kit__kit_number,
                r?.product_snapshot?.kit__seller__nome,
            ]
                .filter(Boolean)
                .join(" • ")
                .toLowerCase();

            return hay.includes(q);
        });
    }, [rows, filters.q]);

    const handleCreatedOrUpdated = async () => {
        await Swal.fire({
            icon: "success",
            title: "Garantia salva com sucesso!",
            text: "A lista foi atualizada abaixo.",
            confirmButtonText: "OK",
        });

        setEditingWarranty(null);
        fetchWarranties();
        // volta pro estado inicial e recarrega produtos
        if (typeof onDone === "function") {
            await onDone();
        }
    };

    const handleEditRow = (row) => {
        setEditingWarranty(row);
        // opcional: rolar até o form
        // window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const effectiveSelectedProduct = useMemo(() => {
        // Em edição, o snapshot da garantia prevalece.
        // Isso evita editar uma garantia usando o produto
        // atualmente selecionado no topo.
        if (editingWarranty) {
            return (
                editingWarranty?.resolved_product ??
                buildSelectedProductFromSnapshot(
                    editingWarranty
                )
            );
        }

        if (selectedProduct?.pk != null) {
            return selectedProduct;
        }

        return null;
    }, [editingWarranty, selectedProduct]);

    const effectiveSeller = useMemo(() => {
        return buildSellerFromWarranty(editingWarranty, seller);
    }, [editingWarranty, seller]);

    const canShowForm = !!(effectiveSeller?.id && effectiveSelectedProduct?.pk);

    return (
        <Box>
            {/* FORM (create/edit) */}
            {canShowForm && (
                <WarrantyCreateForm
                    seller={effectiveSeller}
                    selectedProduct={effectiveSelectedProduct}
                    warranty={editingWarranty} // ✅ edita quando clicar na tabela
                    theme={theme}
                    colors={colors}
                    subtleBorder={subtleBorder}
                    softText={softText}
                    fmtBRL={fmtBRL}
                    onSuccess={handleCreatedOrUpdated}
                    onCancelEdit={editingWarranty ? () => setEditingWarranty(null) : undefined}
                />
            )}

            <Divider sx={{ my: 3 }} />

            {/* HEADER + FILTROS */}
            <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 2, flexWrap: "wrap" }}>
                    <Typography variant="subtitle1" fontWeight={900}>
                        Histórico {seller?.id ? `— ${seller?.nome || ""}` : "(todas as vendedoras)"}
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        {editingWarranty && (
                            <Button size="small" variant="outlined" color="warning" onClick={() => setEditingWarranty(null)}>
                                Nova garantia
                            </Button>
                        )}

                        <Button size="small" variant="outlined" color="warning" onClick={fetchWarranties} disabled={loading}>
                            Recarregar
                        </Button>
                    </Stack>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                    <TextField
                        size="small"
                        select
                        label="Status"
                        value={filters.status}
                        onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                        sx={{ minWidth: 160 }}
                    >
                        {STATUS_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        size="small"
                        label="Lote"
                        value={filters.lot_code}
                        onChange={(e) => setFilters((p) => ({ ...p, lot_code: e.target.value }))}
                        placeholder="Ex: LOTETESTE"
                        sx={{ minWidth: 180 }}
                    />

                    <TextField
                        size="small"
                        label="Buscar"
                        value={filters.q}
                        onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
                        placeholder="Número, código, problema..."
                        sx={{ minWidth: 260, flex: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button
                        size="small"
                        variant="outlined"
                        color="info"
                        onClick={() => setFilters({ status: "", lot_code: "", q: "" })}
                    >
                        Limpar filtros
                    </Button>
                </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <WarrantyListTable
                rows={filteredRows}
                loading={loading}
                error={error}
                subtleBorder={subtleBorder}
                softText={softText}
                onEdit={(w) => {
                    // ✅ sobe pro page selecionar produto e abrir card
                    onEditWarranty?.(w);

                    // ✅ e aqui dentro você também coloca o form em modo edição
                    setEditingWarranty(w); // (se você tiver esse estado no section)
                }}
            />
        </Box>
    );
}
